import { Box, Grid } from '@mui/material'
import { appUserColsMap, AppUserListColumns } from 'components/core/app-user/C1AppUsersList'
import { C1TableRedux } from 'components/core/list/C1Table/C1TableRedux'
import List from 'components/core/list/index'
import { SelectOneAppHook } from 'components/core/search/apps'
import { TextField } from 'components/core/textfield'
import { useErrorReporting } from 'components/no-ui/error-reporting/datadog'
import { QUERIES } from 'components/pages/explore/query/Query'
import { StructuredSearchStyles } from 'components/pages/explore/query/styles'
import { AppValue } from 'lib/actions/apps'
import { AppUserValue } from 'lib/actions/apps/app-users'
import { UserValue } from 'lib/actions/user/index'
import { useInitialFilters } from 'lib/search'
import { useRouter } from 'next/router'
import { SearchInactiveUsersRequest } from 'pbts/c1/api/app/v1/app_usage'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import React, { FC, useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { searchInactiveUsers } from 'redux_toolkit/app-usage'
import { ExportData } from './ExportData'

const PAGE_SIZE_DEFAULT = 50
const RANGE_DAYS_DEFAULT = 30

const InactiveUsersReportTable: FC<{appId: string, days: number}> = ({ appId, days }) => {
    const [page, setPage] = useState<number>(0)
    const { data, isError, isLoading, getPage, canLoadMore, error } = searchInactiveUsers.useSearchInactiveUsers(
        SearchInactiveUsersRequest.create({
            appId,
            days,
            expandMask: { paths: ['*'] },
        }),
        { pageSize: PAGE_SIZE_DEFAULT },
    )

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    useErrorReporting(isError, error, QUERIES.AppUsersNotLoggedInRecently)

    const table = useMemo(
        () => (
            <C1TableRedux<AppUserValue>
                data={data}
                cols={[
                    appUserColsMap[AppUserListColumns.account],
                    appUserColsMap[AppUserListColumns.app],
                    appUserColsMap[AppUserListColumns.status],
                    appUserColsMap[AppUserListColumns.accountType],
                    appUserColsMap[AppUserListColumns.user],
                    appUserColsMap[AppUserListColumns.roles],
                    appUserColsMap[AppUserListColumns.lastLogin],
                    appUserColsMap[AppUserListColumns.actions],
                ]}
                canLoadMore={canLoadMore}
                isError={isError}
                isLoading={isLoading}
                pageSize={PAGE_SIZE_DEFAULT}
                onPageChange={onPageChange}
                error={error}
                page={page}
            />
        ),
        [canLoadMore, data, error, isError, isLoading, onPageChange, page],
    )

    return <List<UserValue> table={table} />
}

export const InactiveUsersReport: FC = () => {
    const intl = useIntl()
    const router = useRouter()

    const initialFilters = useInitialFilters(SearchInactiveUsersRequest)

    const [selectedAppId, setSelectedAppId] = useState<string | undefined>(
        initialFilters.appId as (string | undefined),
    )
    const [days, setDays] = useState<number>(
        parseInt(initialFilters.days as unknown as string, 10) ||
        RANGE_DAYS_DEFAULT,
    )

    const onSelect = useCallback((app:AppValue|null): void => {
        setSelectedAppId(app?.id)
        router.query.appId = app?.id
        router.replace({ pathname: router.pathname, query: router.query }, undefined, { shallow: true })
    }, [router])

    const onNumberChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        if (!event.target.value) {
            return
        }

        const v = parseInt(event.target.value, 10)
        if (isNaN(v) || v < 0) {
            return
        }

        setDays(v)
        router.query.days = v.toString()
        router.replace({ pathname: router.pathname, query: router.query }, undefined, { shallow: true })
    }, [router])

    const exportDataActionRequest = useMemo(() => {
        return ExportDataRequest.create({
            queryId: QueryId.APP_USERS_NOT_LOGGED_IN_RECENTLY,
            request: {
                oneofKind: 'searchInactiveAppUsersRequest',
                searchInactiveAppUsersRequest: SearchInactiveUsersRequest.create({ days, appId: selectedAppId, expandMask: { paths: ['*'] } }),
            },
        })
    }, [selectedAppId, days])

    const shouldGenerateReport = !!(selectedAppId && days > 0)
    return <>
        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
            spacing={2}
            sx={StructuredSearchStyles.filterInputs}
        >
            <Grid item xs={12} md={5} key="select-app-container">
                <SelectOneAppHook onSelected={onSelect} initAppId={initialFilters.appId} sx={StructuredSearchStyles.selectApp} />
            </Grid>
            <Grid item xs={12} md={2} key="select-days-container">
                <TextField
                    value={days}
                    onChange={onNumberChange}
                    label={
                        intl.formatMessage({
                            id: 'components_pages_explore_query_inactive_users_report_select_days_label',
                            defaultMessage: 'Days',
                        })}
                    type="number"
                    size="small"
                    sx={{ width: '100px' }}
                />
            </Grid>
            {
                shouldGenerateReport &&
                <Grid item xs={12} md={5} key="export-data-container" sx={{ height: { md: '100%' } }}>
                    <Box sx={StructuredSearchStyles.downloadReportContainer}>
                        <ExportData exportDataRequest={exportDataActionRequest} />
                    </Box>
                </Grid>
            }
        </Grid>
        {shouldGenerateReport && <InactiveUsersReportTable appId={selectedAppId} days={days} />}
    </>
}
